// src/pages/student/sessions/RescheduleSession.js
import { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { showErrorToast, showSuccessToast } from '../../../components/PluginsMenu/Toastr/Toastr';
import { formatDateToISOString } from '../../../../utils/utils';
import { parseISO } from 'date-fns';
import { useGetSessionDurationQuery, useGetAvailableSlotsQuery, useRescheduleSessionMutation } from '../../../../store/services/student/SessionService';
import { TimeSlotSelection } from './bookSession/book-session-wizard/TimeSlotSelection';
import { SessionDetails } from '../../../components/Sessions/SessionDetails';
import { SessionConfirmationModal } from '../../../components/Sessions/SessionConfirmationModal';
import { useSelector } from 'react-redux';
import { useAdminRescheduleSessionMutation } from '../../../../store/services/admin/SlotPriceService';

export const RescheduleSession = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { sessionId, tutorId } = useParams();
    const [searchParams] = useSearchParams();
    //const tutorId = searchParams.get('tutorId');
    const currentUser = useSelector(state => state.auth.currentUser);

    console.log("RescheduleSession Location State:", location.state);
    console.log("RescheduleSession Params:", searchParams);
    
    // Get session details from location state
    const { 
        currentSessionDate, 
        currentSessionDuration, 
        sessionDurationId,
        subject,
        subjectId
    } = location.state || {};

    const [selectedDate, setSelectedDate] = useState(() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(12, 0, 0, 0);
        return tomorrow;
    });
    
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [optionalMessage, setOptionalMessage] = useState('');
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [allSlots, setAllSlots] = useState([]);
    
    // Get session duration to pre-select it
    const { data: sessionDurations } = useGetSessionDurationQuery();
    const [rescheduleSession] = useRescheduleSessionMutation();
    const [adminRescheduleSession] = useAdminRescheduleSessionMutation();
    
    // Get the duration for the current session
    const selectedDuration = useMemo(() => {
        if (!sessionDurations?.data || !sessionDurationId) return null;
        return sessionDurations.data.find(d => d.id === sessionDurationId);
    }, [sessionDurations, sessionDurationId]);

    // Fetch ALL slots once when tutor and duration are selected
    const { data: allSlotsData, isLoading: isLoadingSlots } = useGetAvailableSlotsQuery(
        {
            TutorId: tutorId,
            SlotPricingId: sessionDurationId, // Use the session's duration ID
        },
        { 
            skip: !tutorId || !sessionDurationId,
            refetchOnMountOrArgChange: false
        }
    );

    // Store all slots when data is fetched
    useEffect(() => {
        if (allSlotsData?.data) {
            setAllSlots(allSlotsData.data);
        }
    }, [allSlotsData]);

    // Filter slots for the selected date
    const filteredSlotsForSelectedDate = useMemo(() => {
        if (!selectedDate || allSlots?.slots?.length === 0) return [];
        
        const formattedSelectedDate = formatDateToISOString(selectedDate);
        const matchedDay = allSlots?.slots?.find(d => d.day === formattedSelectedDate);
        
        return matchedDay?.slots || [];
    }, [selectedDate, allSlots]);

    // Get available days from all slots
    const availableDays = useMemo(() => {
        return allSlots?.slots?.map(d => d.day) || [];
    }, [allSlots]);

    // Get min and max available dates
    const minAvailableDate = useMemo(() => {
        if (availableDays.length > 0) {
            return parseISO(availableDays[0]);
        }
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        return tomorrow;
    }, [availableDays]);

    const maxAvailableDate = useMemo(() => {
        if (availableDays.length > 0) {
            return parseISO(availableDays[availableDays.length - 1]);
        }
        return null;
    }, [availableDays]);

    // Check if a date has available slots
    const dateHasAvailableSlots = (date) => {
        const formattedDate = formatDateToISOString(date);
        const dayData = allSlots?.slots?.find(d => d.day === formattedDate);
        
        if (!dayData) return false;
        
        return dayData.slots.some(slot => slot.sessionStatus === "Available");
    };

    const handleDateSelect = (date) => {
        setSelectedDate(date);
        setSelectedSlot(null);
    };

    const handleSlotClick = (slot) => {
        if (slot === null) {
            setSelectedSlot(null);
        } else {
            setSelectedSlot({ 
                date: selectedDate,
                duration: selectedDuration?.duration, 
                ...slot 
            });
        }
    };

    const removeSelectedSlot = () => {
        setSelectedSlot(null);
    };

    const handleRescheduleSlot = () => {
        if (!selectedSlot) {
            showErrorToast("Please select a new time slot");
            return;
        }
        setShowConfirmationModal(true);
    };

    const handleRescheduleConfirm = async () => {
        setIsProcessing(true);
        
        try {
            const formattedDate = formatDateToISOString(selectedDate);
            
            const reschedulePayload = {
                sessionBookingId: sessionId,
                newDate: formattedDate,
                startTime: selectedSlot.startTime,
                endTime: selectedSlot.endTime
            };

            console.log("Reschedule Payload:", reschedulePayload);

            const response = currentUser.role === "Admin" ? await adminRescheduleSession(reschedulePayload).unwrap() : await rescheduleSession(reschedulePayload).unwrap();

            if (response.succeeded) {
                showSuccessToast("Session rescheduled successfully!");
                setShowConfirmationModal(false);
                
                // Navigate back to session details
                if(currentUser.role === "Admin") 
                    navigate("/sessions");
                else
                    navigate(`/sessions/upcoming`);
            } else {
                throw new Error(response.errors?.join(', ') || 'Reschedule failed');
            }
        } catch (error) {
            console.error("Error rescheduling session:", error);
            showErrorToast(error.data?.errors?.join(', ') || "Failed to reschedule session. Please try again.");
        } finally {
            setIsProcessing(false);
        }
    };

    if ( !sessionId) {
        return (
            <div className="container-fluid py-5">
                <div className="alert alert-warning text-center">
                    <h4>Invalid Reschedule Request</h4>
                    <p>Please select a valid session to reschedule.</p>
                    <button className="btn btn-primary" onClick={() => navigate(-1)}>
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-5">
            <div className="mb-3">
                <button className="btn p-0 d-flex align-items-center" onClick={() => navigate(-1)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#000000" className="bi bi-arrow-left me-2" viewBox="0 0 16 16">
                        <path fillRule="evenodd" d="M15 8a.5.5 0 0 1-.5.5H2.707l4.147 4.146a.5.5 0 0 1-.708.708l-5-5a.5.5 0 0 1 0-.708l5-5a.5.5 0 1 1 .708.708L2.707 7.5H14.5A.5.5 0 0 1 15 8z"/>
                    </svg>
                </button>
            </div>
            
            <Card className="shadow-lg rounded-lg">
                <Card.Header className="bg-warning text-dark py-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <h4 className="mb-0 text-dark">Reschedule Session</h4>
                        <span className="badge bg-dark">
                            Duration: {selectedDuration?.duration || currentSessionDuration} minutes
                        </span>
                    </div>
                </Card.Header>

                <Card.Body>
                    <Row>
                        <Col lg='5'>
                            <div className="calendar-container event-calender dz-calender">
                                <h5 className="mb-3">Select New Date</h5>
                                <DatePicker
                                    selected={selectedDate}
                                    onChange={handleDateSelect}
                                    inline
                                    highlightDates={[selectedDate]}
                                    shouldCloseOnSelect={false}
                                    minDate={(() => {
                                        const tomorrow = new Date();
                                        tomorrow.setDate(tomorrow.getDate() + 1);
                                        tomorrow.setHours(0, 0, 0, 0);
                                        return tomorrow;
                                    })()}
                                    maxDate={maxAvailableDate}
                                    filterDate={(date) => {
                                        const tomorrow = new Date();
                                        tomorrow.setDate(tomorrow.getDate() + 1);
                                        tomorrow.setHours(0, 0, 0, 0);
                                        
                                        const currentDate = new Date(date);
                                        currentDate.setHours(0, 0, 0, 0);
                                        
                                        if (currentDate < tomorrow) return false;
                                        if (maxAvailableDate && currentDate > maxAvailableDate) return false;
                                        
                                        return dateHasAvailableSlots(date);
                                    }}
                                    dayClassName={(date) => {
                                        const currentDate = new Date(date);
                                        currentDate.setHours(0, 0, 0, 0);
                                        
                                        if (!dateHasAvailableSlots(date)) {
                                            return 'text-muted disabled-date';
                                        }
                                        
                                        const formattedDate = formatDateToISOString(date);
                                        const selectedFormattedDate = formatDateToISOString(selectedDate);
                                        
                                        return formattedDate === selectedFormattedDate ? 'selected-date' : '';
                                    }}
                                />
                                {!selectedDate && (
                                    <div className="alert alert-info mt-3">
                                        Please select a date to see available time slots.
                                    </div>
                                )}
                            </div>
                        </Col>
                        
                        <Col lg='7'>
                            {!selectedDate ? (
                                <div className="alert alert-warning">
                                    Please select a date first to see available time slots.
                                </div>
                            ) : isLoadingSlots ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="time-slot-container">
                                    <h5 className="mb-3">Select New Time Slot</h5>
                                    <p className="text-muted mb-4">
                                        Duration: <strong>{selectedDuration?.duration || currentSessionDuration} minutes</strong>
                                    </p>
                                    
                                    <TimeSlotSelection
                                        selectedDuration={selectedDuration}
                                        selectedDate={selectedDate}
                                        handleSlotClick={handleSlotClick}
                                        tutorId={tutorId}
                                        selectedSlot={selectedSlot}
                                        filteredSlots={filteredSlotsForSelectedDate}
                                        isLoadingSlots={isLoadingSlots}
                                    />
                                </div>
                            )}
                        </Col>
                    </Row>
                </Card.Body>

                {selectedSlot && (
                    <SessionDetails
                        selectedSlot={selectedSlot}
                        subject={subject}
                        optionalMessage={optionalMessage}
                        setOptionalMessage={setOptionalMessage}
                        handleBookSlot={handleRescheduleSlot}
                        isBookingLoading={isProcessing}
                        isReschedule={true} // Add flag for reschedule mode
                    />
                )}
            </Card>

            {/* Reschedule Confirmation Modal */}
            <SessionConfirmationModal
                showConfirmationModal={showConfirmationModal}
                isBookingInProgress={isProcessing}
                setShowConfirmationModal={setShowConfirmationModal}
                handleCheckout={handleRescheduleConfirm}
                selectedSlot={selectedSlot}
                subject={subject}
                optionalMessage={optionalMessage}
                isReschedule={true} // Add flag for reschedule mode
            />
        </div>
    );
};